import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useStudio, useLookups, useCatalogModels } from "@/store/StudioContext";
import MediaFrame from "@/components/common/MediaFrame";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { resolveNodeAsset } from "@/domain/canvasAssetResolution";
import { resolveSceneDuration, extractProposalsFromMessages } from "@/domain/productionPlan";
import {
  SlidersHorizontal,
  Film,
  Image as ImageIcon,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Coins,
  Info,
  Check,
  Plus,
  Trash2,
  X,
  User,
  ChevronRight,
} from "lucide-react";
import { Reference } from "@/domain/entities";
import { t } from "@/i18n";
import { toast } from "sonner";

const normalizeModel = (m) => ({
  ...m,
  id: m.model_id ?? m.id,
  provider: m.provider_id ?? m.provider,
});

/**
 * NodeConfigModalContent
 * Componente interno que inicializa y gestiona el estado draft local mientras el modal está abierto.
 */
const NodeConfigModalContent = ({
  open,
  onClose,
  node,
  data,
  onRun,
  hasOperational,
}) => {
  const { state, dispatch } = useStudio();
  const { assetById, referenceById, characterById } = useLookups();

  // Estados locales draft (patrón Guardar configuración / Cancelar)
  const [draftModelId, setDraftModelId] = useState(node.data?.model_id || "");
  const [draftRatio, setDraftRatio] = useState(node.data?.aspect_ratio || "");
  const [draftResolution, setDraftResolution] = useState(node.data?.resolution || "");
  const [draftDuration, setDraftDuration] = useState(
    node.data?.duration != null && node.data?.duration !== "" ? Number(node.data.duration) : ""
  );
  const [draftSceneId, setDraftSceneId] = useState(node.data?.sceneId || "");
  const [draftNodeRefIds, setDraftNodeRefIds] = useState(() =>
    Array.isArray(node.data?.reference_ids)
      ? [...node.data.reference_ids]
      : Array.isArray(node.data?.referenceIds)
      ? [...node.data.referenceIds]
      : []
  );
  const [draftExcludedSceneRefIds, setDraftExcludedSceneRefIds] = useState(() =>
    Array.isArray(node.data?.excluded_scene_ref_ids)
      ? [...node.data.excluded_scene_ref_ids]
      : []
  );
  const [showAddRefModal, setShowAddRefModal] = useState(false);
  const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);
  const [confirmDurationOpen, setConfirmDurationOpen] = useState(false);

  const rawImageModels = useCatalogModels("image");
  const rawVideoModels = useCatalogModels("video");
  const imageModels = rawImageModels
    .filter((m) => (m.model_kind || "").toLowerCase() === "image")
    .map(normalizeModel);
  const videoModels = rawVideoModels
    .filter((m) => (m.model_kind || "").toLowerCase() === "video")
    .map(normalizeModel);

  const isImage = node.type === "image";
  const isVideo = node.type === "video";

  // 1. Toma vinculada y Ratio del Proyecto
  const currentScene = data.scenes.find((s) => s.id === draftSceneId);
  const isSceneMissing = Boolean(draftSceneId && !currentScene);
  const sceneIndex = currentScene
    ? data.scenes.findIndex((s) => s.id === currentScene.id) + 1
    : null;

  // 1b. Duración de montaje de la toma (reutiliza mecanismo de procedencia de Plan de Producción)
  const threadMessages =
    data.thread?.messages ||
    state.threads?.find((th) => th.projectId === data.project?.id)?.messages ||
    [];
  const allProposals = [
    ...(data.proposals || []),
    ...extractProposalsFromMessages(threadMessages),
  ];
  const resolvedMontage = resolveSceneDuration(currentScene, {
    proposals: allProposals,
    activeScript: data.activeScript,
    projectId: data.project?.id,
  });

  const montageDurationLabel = (() => {
    if (!currentScene) return "Sin definir";
    if (resolvedMontage.isSaved) {
      return `${resolvedMontage.duration}s`;
    }
    if (resolvedMontage.isRecovered) {
      return `${resolvedMontage.duration} s previstos — pendientes de consolidar en la escena`;
    }
    return "Sin definir";
  })();

  const projectRatio =
    data.project?.aspectRatio ||
    data.project?.aspect_ratio ||
    data.project?.format ||
    "16:9";
  const sceneRatio =
    currentScene?.aspectRatio ||
    currentScene?.aspect_ratio ||
    currentScene?.format ||
    null;
  const inheritedRatio = sceneRatio || projectRatio;
  const effectiveRatio = draftRatio || inheritedRatio;
  const hasExplicitRatio = Boolean(draftRatio);
  const isRatioDifferentFromProject = effectiveRatio !== projectRatio;

  // 2. Modelo de generación y compatibilidad
  const availableModels = isImage ? imageModels : isVideo ? videoModels : [];
  const selectedModel = availableModels.find((m) => m.id === draftModelId) || null;
  const compatibleResolutions = selectedModel?.resolutions || [];
  const isResolutionIncompatible = Boolean(
    selectedModel &&
      compatibleResolutions.length > 0 &&
      draftResolution &&
      !compatibleResolutions.includes(draftResolution)
  );
  const effectiveResolution = draftResolution || (selectedModel ? selectedModel.default_resolution || compatibleResolutions[0] || "" : "");

  // Duraciones admitidas del catálogo para vídeo
  const modelDurations = (() => {
    if (!selectedModel || !isVideo) return [];
    if (Array.isArray(selectedModel.durations) && selectedModel.durations.length > 0) {
      return selectedModel.durations.map(Number);
    }
    if (
      typeof selectedModel.min_duration === "number" &&
      typeof selectedModel.max_duration === "number" &&
      selectedModel.max_duration >= selectedModel.min_duration
    ) {
      return Array.from(
        { length: selectedModel.max_duration - selectedModel.min_duration + 1 },
        (_, i) => selectedModel.min_duration + i
      );
    }
    return [];
  })();

  const isDurationsMissing = Boolean(
    isVideo && selectedModel && modelDurations.length === 0
  );

  const isDurationIncompatible = Boolean(
    isVideo &&
      selectedModel &&
      modelDurations.length > 0 &&
      draftDuration !== "" &&
      !modelDurations.includes(Number(draftDuration))
  );

  const effectiveGeneratedDuration =
    draftDuration !== "" && !isDurationIncompatible
      ? Number(draftDuration)
      : null;

  const generatedDurationLabel = (() => {
    if (!selectedModel) return "Selecciona un modelo";
    if (isDurationsMissing) return "Sin duraciones admitidas en catálogo";
    if (isDurationIncompatible) return `${draftDuration}s (Incompatible)`;
    if (effectiveGeneratedDuration != null) return `${effectiveGeneratedDuration}s`;
    return "Pendiente de elegir";
  })();

  // 3. Prompt efectivo
  const textInputs = data.edges
    .filter((e) => e.targetId === node.id)
    .map((e) => data.nodes.find((n) => n.id === e.sourceId))
    .filter((n) => n && n.type === "text");
  const effectivePrompt = [
    currentScene?.prompt,
    ...textInputs.map((tNode) => tNode.data?.text),
  ]
    .filter(Boolean)
    .join("\n");

  // 4. Referencias visuales para generar (imágenes guía)
  // Origen 1: Toma (Storyboard), excluyendo las retiradas en este nodo
  const sceneRefIds = Array.isArray(currentScene?.referenceIds) ? currentScene.referenceIds : [];
  const sceneRefs = sceneRefIds
    .filter((rid) => !draftExcludedSceneRefIds.includes(rid))
    .map((rid) => {
      const ref = referenceById[rid] || state.references?.find((r) => r.id === rid);
      if (!ref) return null;
      const char = ref.characterId
        ? characterById[ref.characterId] || state.characters?.find((c) => c.id === ref.characterId)
        : null;
      const asset = assetById[ref.assetId] || state.assets?.find((a) => a.id === ref.assetId);
      return {
        id: ref.id,
        refId: ref.id,
        label: ref.label || (char ? `@${char.handle}` : "Referencia"),
        characterName: char?.name || null,
        characterHandle: char?.handle || (ref.label?.startsWith("@") ? ref.label.split(" ")[0] : null),
        viewLabel: ref.label?.includes("—") ? ref.label.split("—")[1]?.trim() : (char ? "Vista principal" : "Ficha"),
        asset,
        source: "scene",
      };
    })
    .filter(Boolean);

  // Origen 2: Añadidas únicamente a este nodo
  const nodeRefs = draftNodeRefIds
    .map((rid) => {
      const ref = referenceById[rid] || state.references?.find((r) => r.id === rid);
      if (!ref) return null;
      const char = ref.characterId
        ? characterById[ref.characterId] || state.characters?.find((c) => c.id === ref.characterId)
        : null;
      const asset = assetById[ref.assetId] || state.assets?.find((a) => a.id === ref.assetId);
      return {
        id: ref.id,
        refId: ref.id,
        label: ref.label || (char ? `@${char.handle}` : "Referencia nodo"),
        characterName: char?.name || null,
        characterHandle: char?.handle || (ref.label?.startsWith("@") ? ref.label.split(" ")[0] : null),
        viewLabel: ref.label?.includes("—") ? ref.label.split("—")[1]?.trim() : (char ? "Vista principal" : "Ficha"),
        asset,
        source: "node",
      };
    })
    .filter(Boolean);

  // Origen 3: Nodos de referencia conectados en el lienzo
  const connectedRefEdges = data.edges.filter(
    (e) => e.targetId === node.id && data.nodes.find((n) => n.id === e.sourceId)?.type === "reference"
  );
  const canvasRefs = connectedRefEdges
    .map((e) => {
      const sourceNode = data.nodes.find((n) => n.id === e.sourceId);
      const refId = sourceNode?.data?.referenceId;
      const ref = referenceById[refId] || state.references?.find((r) => r.id === refId);
      if (!ref) return null;
      const char = ref.characterId
        ? characterById[ref.characterId] || state.characters?.find((c) => c.id === ref.characterId)
        : null;
      const asset = assetById[ref.assetId] || state.assets?.find((a) => a.id === ref.assetId);
      return {
        id: ref.id,
        refId: ref.id,
        label: ref.label || (char ? `@${char.handle}` : "Referencia lienzo"),
        characterName: char?.name || null,
        characterHandle: char?.handle || (ref.label?.startsWith("@") ? ref.label.split(" ")[0] : null),
        viewLabel: ref.label?.includes("—") ? ref.label.split("—")[1]?.trim() : (char ? "Vista principal" : "Ficha"),
        asset,
        source: "canvas",
      };
    })
    .filter(Boolean);

  // Unificar referencias activas sin duplicar
  const assignedReferences = [];
  const seenRefIds = new Set();
  for (const item of [...sceneRefs, ...nodeRefs, ...canvasRefs]) {
    if (!seenRefIds.has(item.id)) {
      seenRefIds.add(item.id);
      assignedReferences.push(item);
    }
  }

  // Validación de referencias según el modelo
  const modelSupportsVisualRefs = Boolean(
    selectedModel &&
      (selectedModel.supports_references === true ||
        (selectedModel.veyra_max_references != null && selectedModel.veyra_max_references > 0) ||
        selectedModel.reference_supported === true)
  );

  const maxAllowedRefs = selectedModel
    ? selectedModel.max_references ??
      selectedModel.maxReferences ??
      (modelSupportsVisualRefs ? 2 : 0)
    : 2;

  const isReferenceNotSupported = Boolean(
    selectedModel &&
      !modelSupportsVisualRefs &&
      assignedReferences.length > 0
  );
  const isReferenceLimitExceeded = Boolean(
    selectedModel && modelSupportsVisualRefs && assignedReferences.length > maxAllowedRefs
  );
  const isRefsValid = !isReferenceNotSupported && !isReferenceLimitExceeded;

  // Incompatibilidad específica de referencias visuales en vídeo
  const isVideoRefsIncompatible = Boolean(
    isVideo &&
      selectedModel &&
      assignedReferences.length > 0 &&
      !modelSupportsVisualRefs
  );

  // Recursos autorizados del proyecto para el modal de añadir
  const projectCharacters = data.characters || [];
  const projectReferences = data.references || [];

  // 5. Para vídeo: Imagen de entrada (Start Frame / End Frame) y Modalidades
  const connectedImgEdges = data.edges.filter(
    (e) => e.targetId === node.id && data.nodes.find((n) => n.id === e.sourceId)?.type === "image"
  );
  const startEdge =
    connectedImgEdges.find((e) => e.role === "start_frame") || connectedImgEdges[0];
  const endEdge =
    connectedImgEdges.find((e) => e.role === "end_frame") ||
    (connectedImgEdges.length > 1 ? connectedImgEdges[1] : null);

  const connectedStartNode = startEdge
    ? data.nodes.find((x) => x.id === startEdge.sourceId)
    : null;
  const connectedStartAsset = connectedStartNode
    ? resolveNodeAsset(connectedStartNode, data, assetById)
    : null;

  const sceneActiveAsset = currentScene?.selectedAssetId
    ? assetById[currentScene.selectedAssetId]
    : null;
  const sceneHasValidImage = sceneActiveAsset && sceneActiveAsset.type === "image";

  let effectiveInputImage = null;
  let inputImageOrigin = "none";
  let isConnectedImageMissing = false;

  if (connectedStartNode) {
    if (connectedStartAsset) {
      effectiveInputImage = connectedStartAsset;
      inputImageOrigin = "connected";
    } else {
      isConnectedImageMissing = true;
    }
  } else if (sceneHasValidImage) {
    effectiveInputImage = sceneActiveAsset;
    inputImageOrigin = "scene_fallback";
  }

  // Modalidades de vídeo
  const modelModes = selectedModel?.modes || [];
  const supportsT2V = modelModes.includes("text_to_video");
  const supportsI2V =
    modelModes.includes("image_to_video") || modelModes.includes("first_last_frame");

  // Incompatibilidad: Modelo exclusivo T2V pero se proporciona imagen de entrada
  const isInputImageNotSupported = Boolean(
    isVideo &&
      selectedModel &&
      modelModes.length > 0 &&
      !supportsI2V &&
      effectiveInputImage
  );

  // Incompatibilidad: Modelo requiere Start Frame (I2V) pero falta imagen
  const isInputImageRequired = Boolean(
    isVideo &&
      selectedModel &&
      modelModes.length > 0 &&
      !supportsT2V &&
      !effectiveInputImage
  );

  // Incompatibilidad: Conexión End Frame con modelo que no admite first_last_frame
  const isFLFNotSupported = Boolean(
    isVideo &&
      selectedModel &&
      endEdge &&
      modelModes.length > 0 &&
      !modelModes.includes("first_last_frame")
  );

  // 6. Estimación de Coste
  const costInfo = (() => {
    if (!selectedModel) {
      return { text: "Selecciona un modelo para calcular estimación.", valid: false };
    }
    if (selectedModel.pending_integration || selectedModel.pendingIntegration) {
      return { text: "Modelo pendiente de integración.", valid: false };
    }
    const isPricePending =
      selectedModel.price_pending === true ||
      selectedModel.pricePending === true ||
      (selectedModel.generation_available === false && !selectedModel.pending_integration);
    if (isPricePending) {
      return {
        text: "Precio pendiente en catálogo — generación no disponible.",
        valid: false,
      };
    }
    if (isVideo && (isDurationIncompatible || effectiveGeneratedDuration == null)) {
      return {
        text: "Selecciona una duración válida para calcular estimación.",
        valid: false,
      };
    }
    if (selectedModel.rate_per_second_usd != null) {
      const dur = effectiveGeneratedDuration || 5;
      const rate = selectedModel.rate_per_second_usd;
      const margin = selectedModel.safety_margin_default || 20;
      const total = dur * rate * (1 + margin / 100);
      return {
        amount: `$${total.toFixed(4)} USD`,
        detail: `${dur}s generados × $${rate}/s (+ ${margin}% margen de retención)`,
        valid: true,
      };
    }
    if (selectedModel.reserve_usd != null) {
      return {
        amount: `$${Number(selectedModel.reserve_usd).toFixed(2)} USD`,
        detail: "Reserva fija de presupuesto",
        valid: true,
      };
    }
    return { text: "Sin tarifa calculable disponible.", valid: false };
  })();

  // 7. Resultado generado del nodo (únicamente para resultados reales del tipo del nodo)
  const rawNodeAsset = resolveNodeAsset(node, data, assetById);
  const isRealGeneratedResult =
    Boolean(rawNodeAsset) &&
    (isImage ? rawNodeAsset.type === "image" : rawNodeAsset.type === "video");
  const nodeResultAsset = isRealGeneratedResult ? rawNodeAsset : null;
  const isResultAssignedToScene =
    Boolean(nodeResultAsset) &&
    Boolean(currentScene) &&
    currentScene.selectedAssetId === nodeResultAsset.id;

  // Acciones de referencias
  const handleRemoveReference = (refItem) => {
    if (refItem.source === "node") {
      setDraftNodeRefIds((prev) => prev.filter((id) => id !== refItem.id));
    } else if (refItem.source === "scene") {
      // Excluir de este nodo sin alterar silenciosamente el Storyboard
      setDraftExcludedSceneRefIds((prev) => [...prev, refItem.id]);
    } else if (refItem.source === "canvas") {
      const edge = data.edges.find(
        (e) =>
          e.targetId === node.id &&
          data.nodes.find((n) => n.id === e.sourceId)?.data?.referenceId === refItem.id
      );
      if (edge) {
        dispatch({ type: "EDGE_DELETE", payload: { id: edge.id } });
      }
    }
  };

  const handleAddCharacterView = (char, view) => {
    if (!view.assetId) return;
    let existingRef = (state.references || []).find(
      (r) =>
        r.characterId === char.id &&
        r.assetId === view.assetId &&
        (r.projectId === data.project.id || r.scope === "global")
    );
    if (!existingRef) {
      existingRef = Reference({
        kind: "character",
        characterId: char.id,
        assetId: view.assetId,
        label: `@${char.handle} — ${view.label || "Vista"}`,
        projectId: data.project.id,
        scope: "project",
      });
      dispatch({ type: "REFERENCE_CREATE", payload: existingRef });
    }

    if (draftExcludedSceneRefIds.includes(existingRef.id)) {
      setDraftExcludedSceneRefIds((prev) => prev.filter((id) => id !== existingRef.id));
    } else if (!draftNodeRefIds.includes(existingRef.id)) {
      setDraftNodeRefIds((prev) => [...prev, existingRef.id]);
    }
    setShowAddRefModal(false);
    toast.success(`Referencia de @${char.handle} añadida al nodo.`);
  };

  const handleAddDirectRef = (ref) => {
    if (draftExcludedSceneRefIds.includes(ref.id)) {
      setDraftExcludedSceneRefIds((prev) => prev.filter((id) => id !== ref.id));
    } else if (!draftNodeRefIds.includes(ref.id)) {
      setDraftNodeRefIds((prev) => [...prev, ref.id]);
    }
    setShowAddRefModal(false);
    toast.success(`Referencia "${ref.label}" añadida al nodo.`);
  };

  // Cambio de modelo: conserva la duración anterior solo si es compatible;
  // si no lo es, pide elegir otra sin sustituirla silenciosamente.
  const handleModelSelect = (newModelId) => {
    setDraftModelId(newModelId);
    if (!newModelId) return;
    const newModel = availableModels.find((m) => m.id === newModelId);
    if (!newModel) return;

    if (newModel.resolutions && newModel.resolutions.length > 0) {
      if (draftResolution && !newModel.resolutions.includes(draftResolution)) {
        setDraftResolution(newModel.default_resolution || newModel.resolutions[0]);
      }
    }
  };

  // Guardar configuración en el nodo (sin ejecutar ni alterar Storyboard)
  const handleSaveConfig = () => {
    const patch = {
      model_id: draftModelId || null,
      aspect_ratio: hasExplicitRatio ? draftRatio : null,
      resolution: draftResolution || null,
      duration: isVideo ? (effectiveGeneratedDuration ?? (draftDuration !== "" ? Number(draftDuration) : null)) : null,
      reference_ids: draftNodeRefIds,
      excluded_scene_ref_ids: draftExcludedSceneRefIds,
      sceneId: draftSceneId || null,
    };
    dispatch({
      type: "NODE_UPDATE",
      payload: { id: node.id, data: patch },
    });
    toast.success("Configuración del nodo guardada.");
    onClose();
  };

  // Asignar resultado a la toma vinculada
  const handleAssignToSceneClick = () => {
    if (!currentScene || !nodeResultAsset) return;
    if (currentScene.selectedAssetId && currentScene.selectedAssetId !== nodeResultAsset.id) {
      setReplaceConfirmOpen(true);
    } else {
      executeAssignMedia();
    }
  };

  const executeAssignMedia = () => {
    if (!currentScene || !nodeResultAsset) return;
    dispatch({
      type: "SCENE_SELECT_ASSET",
      payload: { sceneId: currentScene.id, assetId: nodeResultAsset.id },
    });
    dispatch({
      type: "SELECT",
      payload: {
        projectId: data.project.id,
        patch: { sceneId: currentScene.id, assetId: nodeResultAsset.id },
      },
    });
  };

  const canExecute = Boolean(
    hasOperational &&
      selectedModel &&
      !isResolutionIncompatible &&
      (!isVideo || (!isDurationsMissing && !isDurationIncompatible && effectiveGeneratedDuration != null)) &&
      isRefsValid &&
      !isVideoRefsIncompatible &&
      !isInputImageNotSupported &&
      !isInputImageRequired &&
      !isFLFNotSupported &&
      !isSceneMissing
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          data-testid="node-config-modal"
          className="max-w-2xl max-h-[85vh] overflow-y-auto border-[#333] bg-[#141414] text-[#ededed] p-6 shadow-2xl"
        >
          <DialogHeader className="pb-3 border-b border-[#262626]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <SlidersHorizontal size={18} className="text-[#f5b544]" />
                <DialogTitle className="text-base font-semibold text-[#ededed]">
                  Configuración del nodo · {isImage ? "Imagen" : isVideo ? "Vídeo" : node.type}
                </DialogTitle>
                <span className="font-mono text-xs text-[#737373]">({node.id})</span>
              </div>
            </div>
          </DialogHeader>

          <div className="mt-4 flex flex-col gap-4 text-xs">
            {/* 1. Proyecto y Toma vinculada */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a]">
              <div>
                <span className="text-[10px] font-mono uppercase text-[#737373]">Proyecto:</span>
                <div className="font-medium text-[#d8d4cd] truncate">{data.project.name}</div>
                <div className="font-mono text-[10px] text-[#666]">
                  {data.project.id} · Formato base: {projectRatio}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono uppercase text-[#737373]">Toma vinculada:</span>
                  {isSceneMissing && (
                    <span
                      data-testid="node-scene-invalid-badge"
                      className="inline-flex items-center gap-1 rounded bg-red-950/60 border border-red-500/40 px-1.5 py-0.5 text-[10px] text-red-300"
                    >
                      <AlertTriangle size={10} /> Toma eliminada
                    </span>
                  )}
                </div>
                <select
                  data-testid="node-config-scene-select"
                  className="v-input h-7 text-xs mt-1 w-full"
                  value={draftSceneId}
                  onChange={(e) => setDraftSceneId(e.target.value || "")}
                >
                  <option value="">{t.canvas.unlinked || "Sin vincular a toma"}</option>
                  {data.scenes.map((s, idx) => (
                    <option key={s.id} value={s.id}>
                      {idx + 1}. {s.title || t.common.untitled}
                    </option>
                  ))}
                </select>
                {currentScene && (
                  <span className="text-[10px] text-[#888] mt-0.5 block truncate">
                    Toma {sceneIndex}: {currentScene.title || t.common.untitled}
                  </span>
                )}
              </div>
            </div>

            {/* 2. Operación, Modelo y Formato */}
            <div className="p-3 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#a3a3a3] flex items-center gap-1.5">
                  {isImage ? <ImageIcon size={13} className="text-[#f5b544]" /> : <Film size={13} className="text-[#cb8fb9]" />}
                  Operación: {isImage ? "Generación de imagen base" : "Generación de vídeo"}
                </span>
                <span className="font-mono text-[10px] text-[#737373]">
                  Proveedor: {selectedModel?.provider || (selectedModel ? "APIMart" : "—")}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
                {/* Selector de Modelo */}
                <div>
                  <label className="text-[10px] text-[#888] block mb-1">Modelo de generación:</label>
                  <select
                    data-testid="node-config-model-select"
                    className="v-input h-8 text-xs w-full"
                    value={draftModelId}
                    onChange={(e) => handleModelSelect(e.target.value)}
                  >
                    <option value="">— Seleccionar modelo —</option>
                    {availableModels.map((m) => {
                      const isPending = m.pending_integration || m.pendingIntegration;
                      const isPricePending =
                        !isPending &&
                        (m.price_pending === true ||
                          m.pricePending === true ||
                          (m.generation_available === false && !isPending));
                      return (
                        <option key={m.id} value={m.id} disabled={isPending || isPricePending}>
                          {m.label || m.id}{" "}
                          {isPending ? "[Pendiente]" : isPricePending ? "[Precio pendiente]" : ""}
                        </option>
                      );
                    })}
                  </select>
                  {!selectedModel && (
                    <span className="text-[10px] text-amber-400 mt-1 block">
                      Selecciona un modelo para habilitar resolución y coste.
                    </span>
                  )}
                </div>

                {/* Parámetros: Resolución y Ratio */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-[#888] block mb-1">Resolución:</label>
                    <select
                      data-testid="node-config-resolution-select"
                      disabled={!selectedModel}
                      className={`v-input h-8 text-xs w-full ${!selectedModel ? "opacity-50 cursor-not-allowed" : ""}`}
                      value={draftResolution}
                      onChange={(e) => setDraftResolution(e.target.value)}
                    >
                      <option value="">
                        {selectedModel ? "— Seleccionar resolución —" : "— Selecciona modelo —"}
                      </option>
                      {compatibleResolutions.map((res) => (
                        <option key={res} value={res}>
                          {res}
                        </option>
                      ))}
                    </select>
                    {isResolutionIncompatible && (
                      <span
                        data-testid="node-config-resolution-incompatible"
                        className="text-[9px] text-amber-400 mt-0.5 block"
                      >
                        ⚠️ Incompatible con {selectedModel?.label}. Selecciona una válida.
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="text-[10px] text-[#888] block mb-1">Relación (Ratio):</label>
                    <select
                      data-testid="node-config-ratio-select"
                      className="v-input h-8 text-xs w-full"
                      value={draftRatio || effectiveRatio}
                      onChange={(e) => setDraftRatio(e.target.value)}
                    >
                      <option value="9:16">9:16 (Vertical)</option>
                      <option value="16:9">16:9 (Horizontal)</option>
                      <option value="1:1">1:1 (Cuadrado)</option>
                    </select>
                    {hasExplicitRatio && isRatioDifferentFromProject ? (
                      <span
                        data-testid="node-config-ratio-diff"
                        className="text-[9px] text-amber-400 mt-0.5 block truncate"
                        title={`Difiere del formato del proyecto (${projectRatio})`}
                      >
                        Difiere del proyecto ({projectRatio})
                      </span>
                    ) : (
                      <span
                        data-testid="node-config-ratio-inherited"
                        className="text-[9px] text-[#737373] mt-0.5 block truncate"
                      >
                        Formato del proyecto ({projectRatio})
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Referencias Visuales para Generar */}
            <div className="p-3 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[#a3a3a3] block">
                    Referencias visuales para generar ({assignedReferences.length})
                  </span>
                  <span className="text-[10px] text-[#737373]">
                    Imágenes guía de estilo o identidad de personaje. Máximo permitido: {maxAllowedRefs}.
                  </span>
                </div>
                <button
                  type="button"
                  data-testid="node-config-add-ref-btn"
                  onClick={() => setShowAddRefModal(true)}
                  className="v-btn-secondary v-btn-sm text-[11px] flex items-center gap-1.5"
                >
                  <Plus size={12} /> Añadir referencia
                </button>
              </div>

              {/* Avisos de validación de referencias */}
              {isReferenceNotSupported && (
                <div
                  data-testid="node-config-refs-not-supported"
                  className="flex items-start gap-2 p-2 rounded bg-amber-950/30 border border-amber-500/40 text-amber-300 text-[11px]"
                >
                  <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-400" />
                  <span>
                    El modelo {isVideo ? "de vídeo " : ""}«{selectedModel?.label}» no admite referencias visuales adicionales de personaje o estilo{isVideo ? " además de la imagen de entrada (Start Frame)" : ""}. No descartes referencias silenciosamente: quita las referencias asignadas para poder ejecutar.
                  </span>
                </div>
              )}
              {isReferenceLimitExceeded && (
                <div
                  data-testid="node-config-refs-limit-exceeded"
                  className="flex items-start gap-2 p-2 rounded bg-amber-950/30 border border-amber-500/40 text-amber-300 text-[11px]"
                >
                  <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-400" />
                  <span>
                    Se han asignado {assignedReferences.length} referencias visuales, pero el modelo {selectedModel?.label} admite un máximo de {maxAllowedRefs}. Quita referencias para poder ejecutar.
                  </span>
                </div>
              )}

              {/* Lista de Referencias asignadas */}
              {assignedReferences.length === 0 ? (
                <p data-testid="node-config-no-refs" className="text-[11px] text-[#737373] italic py-1">
                  Sin referencias visuales asignadas para esta generación.
                </p>
              ) : (
                <div data-testid="node-config-refs-list" className="flex flex-wrap gap-2 mt-1">
                  {assignedReferences.map((refItem) => (
                    <div
                      key={refItem.id}
                      data-testid={`assigned-ref-card-${refItem.id}`}
                      className="flex items-center gap-2 rounded bg-[#101010] border border-[#2a2a2a] p-1.5 pr-2"
                    >
                      <div className="h-9 w-9 rounded overflow-hidden bg-black flex-shrink-0 border border-[#333]">
                        <MediaFrame asset={refItem.asset} fit="cover" className="h-full w-full" />
                      </div>
                      <div className="flex flex-col text-[10px] min-w-0 max-w-[170px]">
                        <span className="font-medium text-[#ededed] truncate">
                          {refItem.characterName || refItem.label}
                        </span>
                        {refItem.characterHandle && (
                          <span className="font-mono text-[9px] text-[#f5b544]">
                            @{refItem.characterHandle.replace(/^@+/, "")}
                          </span>
                        )}
                        <span className="text-[9px] text-[#888] truncate">
                          {refItem.viewLabel}
                        </span>
                        <div className="mt-0.5">
                          {refItem.source === "scene" ? (
                            <span
                              data-testid={`ref-source-badge-${refItem.id}`}
                              className="rounded bg-sky-950/60 border border-sky-500/40 px-1 py-0.2 text-[8px] text-sky-300"
                            >
                              Asignada a la toma
                            </span>
                          ) : refItem.source === "node" ? (
                            <span
                              data-testid={`ref-source-badge-${refItem.id}`}
                              className="rounded bg-amber-950/60 border border-amber-500/40 px-1 py-0.2 text-[8px] text-amber-300"
                            >
                              Añadida al nodo
                            </span>
                          ) : (
                            <span
                              data-testid={`ref-source-badge-${refItem.id}`}
                              className="rounded bg-purple-950/60 border border-purple-500/40 px-1 py-0.2 text-[8px] text-purple-300"
                            >
                              Conexión lienzo
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid={`ref-remove-btn-${refItem.id}`}
                        onClick={() => handleRemoveReference(refItem)}
                        className="rounded p-1 text-[#737373] hover:text-red-400 hover:bg-red-950/30 transition-colors ml-1"
                        title={refItem.source === "scene" ? "Excluir de este nodo" : "Quitar referencia"}
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 4. Para Vídeo: Entrada de vídeo (Start Frame) y Duraciones */}
            {isVideo && (
              <div
                data-testid="node-config-video-inputs"
                className="p-3 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] flex flex-col gap-2.5"
              >
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#a3a3a3] flex items-center gap-1.5">
                  <Film size={13} className="text-[#cb8fb9]" />
                  Entrada de vídeo (Start Frame) y Duraciones
                </span>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-2.5 rounded bg-[#101010] border border-[#262626]">
                  {/* Columna 1: Duración de montaje */}
                  <div>
                    <span className="text-[10px] text-[#737373] flex items-center gap-1">
                      <Clock size={11} /> Duración de montaje (Storyboard):
                    </span>
                    <div
                      data-testid="node-config-storyboard-duration"
                      className="font-mono text-xs font-semibold text-[#f5b544] mt-1"
                    >
                      {montageDurationLabel}
                    </div>
                    {resolvedMontage.isRecovered && !resolvedMontage.isSaved && (
                      <div className="mt-1.5">
                        <button
                          type="button"
                          data-testid="confirm-montage-duration-btn"
                          onClick={() => setConfirmDurationOpen(true)}
                          className="inline-flex items-center gap-1 rounded bg-[#f5b544]/15 border border-[#f5b544]/40 px-2 py-0.5 text-[11px] font-semibold text-[#f5b544] hover:bg-[#f5b544]/25 transition-colors cursor-pointer"
                        >
                          <CheckCircle2 size={11} />
                          <span>Confirmar duración de {resolvedMontage.duration} s</span>
                        </button>
                      </div>
                    )}
                    <span className="text-[9px] text-[#666] mt-0.5 block">
                      Tiempo narrativo planificado para la toma.
                    </span>
                  </div>

                  {/* Columna 2: Duración generada / facturable */}
                  <div>
                    <span className="text-[10px] text-[#737373] flex items-center gap-1">
                      <Film size={11} /> Duración generada (Modelo / Facturable):
                    </span>
                    <div
                      data-testid="node-config-generated-duration"
                      className={`font-mono text-xs font-semibold mt-1 ${
                        !selectedModel
                          ? "text-[#888]"
                          : isDurationIncompatible || isDurationsMissing
                          ? "text-amber-400"
                          : "text-emerald-400"
                      }`}
                    >
                      {generatedDurationLabel}
                    </div>

                    {selectedModel && !isDurationsMissing && (
                      <div className="mt-1.5">
                        <select
                          data-testid="node-config-duration-select"
                          className="v-input h-7 text-xs w-full font-mono"
                          value={isDurationIncompatible ? draftDuration : (effectiveGeneratedDuration ?? "")}
                          onChange={(e) => setDraftDuration(e.target.value ? Number(e.target.value) : "")}
                        >
                          <option value="">— Elegir duración válida —</option>
                          {isDurationIncompatible && (
                            <option value={draftDuration} disabled>
                              {draftDuration} s (Incompatible)
                            </option>
                          )}
                          {modelDurations.map((d) => (
                            <option key={d} value={d}>
                              {d} s
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {isDurationsMissing && (
                      <span
                        data-testid="node-config-missing-durations"
                        className="text-[9px] text-amber-400 mt-1 block"
                      >
                        ⚠️ El modelo {selectedModel?.label} no tiene duraciones admitidas configuradas en el catálogo.
                      </span>
                    )}

                    {isDurationIncompatible && (
                      <span
                        data-testid="node-config-duration-incompatible"
                        className="text-[9px] text-amber-400 mt-1 block"
                      >
                        ⚠️ La duración previa ({draftDuration} s) no es compatible con {selectedModel?.label}. Elige una duración admitida.
                      </span>
                    )}

                    <span className="text-[9px] text-[#666] mt-0.5 block">
                      Metraje discreto generado y facturado según catálogo.
                    </span>
                  </div>
                </div>

                {/* Avisos de incompatibilidad de modalidad */}
                {isInputImageNotSupported && (
                  <div
                    data-testid="node-config-input-image-not-supported"
                    className="flex items-start gap-2 p-2 rounded bg-amber-950/30 border border-amber-500/40 text-amber-300 text-[11px]"
                  >
                    <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-400" />
                    <span>
                      El modelo «{selectedModel?.label}» opera exclusivamente en modalidad Text-to-Video (T2V) y no admite imagen de entrada (Start Frame). Desconecta la imagen de entrada o selecciona un modelo compatible con Image-to-Video.
                    </span>
                  </div>
                )}
                {isInputImageRequired && (
                  <div
                    data-testid="node-config-input-image-required"
                    className="flex items-start gap-2 p-2 rounded bg-amber-950/30 border border-amber-500/40 text-amber-300 text-[11px]"
                  >
                    <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-400" />
                    <span>
                      El modelo «{selectedModel?.label}» requiere una imagen de entrada (Start Frame) para Image-to-Video. Asigna una imagen activa a la toma o conecta un nodo Imagen.
                    </span>
                  </div>
                )}
                {isFLFNotSupported && (
                  <div
                    data-testid="node-config-flf-not-supported"
                    className="flex items-start gap-2 p-2 rounded bg-amber-950/30 border border-amber-500/40 text-amber-300 text-[11px]"
                  >
                    <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-400" />
                    <span>
                      El modelo «{selectedModel?.label}» no admite fotograma final (End Frame / First-Last-Frame). Desconecta el End Frame o selecciona un modelo compatible.
                    </span>
                  </div>
                )}

                {/* Imagen de entrada */}
                <div className="mt-1">
                  <span className="text-[10px] uppercase font-mono text-[#737373] block mb-1">
                    Imagen de entrada (Start Frame):
                  </span>
                  {effectiveInputImage ? (
                    <div
                      data-testid="node-config-input-scene"
                      className="flex items-center gap-3 p-2 rounded bg-[#101010] border border-[#262626]"
                    >
                      <div className="h-12 w-16 overflow-hidden rounded bg-black flex-shrink-0">
                        <MediaFrame asset={effectiveInputImage} fit="cover" className="h-full w-full" />
                      </div>
                      <div className="flex flex-col text-[11px] min-w-0">
                        <span className="font-medium text-[#ededed] truncate">
                          {effectiveInputImage.label || effectiveInputImage.filename || "Imagen inicial"}
                        </span>
                        <span className="text-[10px] text-emerald-400 font-mono">
                          {inputImageOrigin === "connected"
                            ? `✓ Entrada desde nodo Imagen conectado (${connectedStartNode?.id})`
                            : "✓ Entrada desde imagen activa de la toma (sin regenerar)"}
                        </span>
                      </div>
                    </div>
                  ) : isConnectedImageMissing ? (
                    <div
                      data-testid="node-config-input-missing"
                      className="flex items-start gap-2 p-2.5 rounded bg-amber-950/25 border border-amber-500/40 text-amber-300"
                    >
                      <AlertTriangle size={14} className="shrink-0 mt-0.5 text-amber-400" />
                      <div>
                        <span className="font-semibold block">Falta imagen de entrada:</span>
                        <span>
                          El nodo Imagen conectado aún no ha generado ningún resultado. Ejecuta primero el nodo Imagen antes de generar este vídeo.
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="p-2 rounded bg-[#101010] border border-[#262626] text-[#888]">
                      Sin imagen de entrada conectada ni en la toma. Si el modelo admite solo I2V, conecta un nodo Imagen o asigna una imagen en Storyboard.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 5. Prompt Efectivo */}
            <div className="p-3 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#a3a3a3]">
                  Prompt efectivo enviado al modelo
                </span>
                <span className="text-[10px] font-mono text-[#737373]">
                  {effectivePrompt.length} caracteres
                </span>
              </div>
              <div
                data-testid="node-config-effective-prompt"
                className="max-h-24 overflow-y-auto rounded bg-[#0f0f0f] p-2.5 font-mono text-[11px] text-[#d8d4cd] whitespace-pre-wrap border border-[#262626]"
              >
                {effectivePrompt || "— (Sin prompt definido)"}
              </div>
              <div className="mt-1.5 flex items-start gap-1.5 text-[10px] text-[#737373] leading-relaxed">
                <Info size={12} className="shrink-0 mt-0.5 text-[#a3a3a3]" />
                <span>
                  Una mención @ en el texto no equivale a una imagen enviada al modelo. Las referencias visuales reales enviadas deben asignarse explícitamente en la sección superior.
                </span>
              </div>
            </div>

            {/* 6. Estimación de Coste */}
            <div className="p-3 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins size={16} className="text-[#f5b544]" />
                <div>
                  <span className="text-[11px] font-semibold text-[#ededed] block">
                    Coste estimado de generación:
                  </span>
                  <span className="text-[10px] text-[#737373]">
                    {costInfo.valid ? costInfo.detail : costInfo.text}
                  </span>
                </div>
              </div>
              {costInfo.valid && (
                <span
                  data-testid="node-config-cost-badge"
                  className="font-mono text-sm font-bold text-[#f5b544]"
                >
                  {costInfo.amount}
                </span>
              )}
            </div>

            {/* 7. Resultado del Nodo y Asignación a la Toma */}
            {nodeResultAsset && (
              <div
                data-testid="node-config-result-section"
                className="p-3 rounded-lg border border-[#383838] bg-[#171717] flex flex-col gap-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[#a3a3a3]">
                    Resultado generado del nodo
                  </span>
                  {isResultAssignedToScene ? (
                    <span
                      data-testid="node-result-assigned-badge"
                      className="inline-flex items-center gap-1 rounded bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-semibold text-emerald-400"
                    >
                      <Check size={11} /> Medio activo de la toma {sceneIndex}
                    </span>
                  ) : (
                    <span className="text-[10px] text-[#888]">Variante generada disponible</span>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-16 w-24 rounded overflow-hidden bg-black flex-shrink-0 border border-[#262626]">
                    <MediaFrame asset={nodeResultAsset} fit="contain" className="h-full w-full" />
                  </div>
                  <div className="flex flex-col text-[11px] min-w-0 flex-1">
                    <span className="font-medium text-[#ededed] truncate">
                      {nodeResultAsset.label || nodeResultAsset.filename || "Resultado"}
                    </span>
                    <span className="font-mono text-[10px] text-[#737373]">
                      ID: {nodeResultAsset.id} · {nodeResultAsset.type}
                    </span>
                    {currentScene && !isResultAssignedToScene && (
                      <button
                        type="button"
                        data-testid="node-assign-to-scene-btn"
                        onClick={handleAssignToSceneClick}
                        className="v-btn-primary v-btn-sm mt-2 self-start"
                      >
                        Asignar a esta toma
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Acciones del Modal: Cancelar / Guardar configuración / Ejecutar */}
          <div className="mt-5 flex items-center justify-end gap-2 pt-3 border-t border-[#262626]">
            <button
              type="button"
              data-testid="node-config-close-btn"
              onClick={onClose}
              className="v-btn-ghost v-btn-sm"
            >
              Cancelar
            </button>
            <button
              type="button"
              data-testid="node-config-save-btn"
              onClick={handleSaveConfig}
              className="v-btn-secondary v-btn-sm"
              title="Guardar cambios en el nodo sin ejecutar generación"
            >
              Guardar configuración
            </button>
            <button
              type="button"
              data-testid="node-config-run-btn"
              disabled={!canExecute}
              onClick={() => {
                handleSaveConfig();
                onRun();
              }}
              className="v-btn-primary v-btn-sm"
              title={
                !selectedModel
                  ? "Selecciona un modelo para generar"
                  : isResolutionIncompatible
                  ? "La resolución seleccionada no es compatible"
                  : isDurationsMissing
                  ? "El modelo no tiene duraciones admitidas configuradas en el catálogo"
                  : isDurationIncompatible
                  ? "La duración previa no es compatible con el modelo"
                  : isVideo && effectiveGeneratedDuration == null
                  ? "Selecciona una duración válida para generar"
                  : isVideoRefsIncompatible
                  ? "El modelo no admite referencias visuales adicionales"
                  : isReferenceNotSupported
                  ? "El modelo no admite referencias visuales"
                  : isReferenceLimitExceeded
                  ? `Se supera el límite de ${maxAllowedRefs} referencias`
                  : isInputImageNotSupported
                  ? "El modelo opera solo en Text-to-Video y no admite Start Frame"
                  : isInputImageRequired
                  ? "Falta la imagen de entrada (Start Frame) requerida para generar vídeo"
                  : isFLFNotSupported
                  ? "El modelo no admite fotograma final (First-Last-Frame)"
                  : isSceneMissing
                  ? "La toma vinculada no es válida"
                  : "Ejecutar generación"
              }
            >
              {t.canvas.run || "Ejecutar"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo para Añadir Referencias desde Recursos del Proyecto */}
      {showAddRefModal && (
        <Dialog open={showAddRefModal} onOpenChange={setShowAddRefModal}>
          <DialogContent
            data-testid="add-ref-dialog"
            className="max-w-lg max-h-[80vh] overflow-y-auto border-[#333] bg-[#161616] text-[#ededed] p-5 shadow-2xl"
          >
            <DialogHeader>
              <DialogTitle className="text-sm font-semibold text-[#ededed] flex items-center gap-2">
                <User size={16} className="text-[#f5b544]" />
                Añadir referencia visual del proyecto
              </DialogTitle>
            </DialogHeader>

            <p className="text-xs text-[#888] mt-1">
              Selecciona una ficha autorizada en los Recursos de este proyecto ({data.project.name}) para guiar la generación.
            </p>

            <div className="mt-3 flex flex-col gap-3">
              <span className="text-[11px] font-semibold uppercase text-[#a3a3a3]">
                Personajes del proyecto ({projectCharacters.length})
              </span>
              {projectCharacters.length === 0 ? (
                <p className="text-xs text-[#737373] italic">No hay personajes registrados en este proyecto.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {projectCharacters.map((char) => {
                    const charMasterAsset =
                      assetById[char.masterAssetId] ||
                      (char.views?.[0]?.assetId ? assetById[char.views[0].assetId] : null);
                    const views =
                      Array.isArray(char.views) && char.views.length > 0
                        ? char.views
                        : char.masterAssetId
                        ? [{ id: "vw_master", assetId: char.masterAssetId, label: "Principal", viewType: "front" }]
                        : [];
                    return (
                      <div
                        key={char.id}
                        data-testid={`project-character-${char.id}`}
                        className="rounded-lg border border-[#262626] bg-[#101010] p-3"
                      >
                        <div className="flex items-center gap-2.5 mb-2">
                          <div className="h-8 w-8 rounded overflow-hidden bg-black shrink-0 border border-[#333]">
                            <MediaFrame asset={charMasterAsset} fit="cover" className="h-full w-full" />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-semibold text-[#ededed] truncate">
                              {char.name || "Sin nombre"}
                            </span>
                            <span className="font-mono text-[10px] text-[#f5b544]">
                              @{char.handle ? char.handle.replace(/^@+/, "") : "personaje"}
                            </span>
                          </div>
                        </div>

                        {/* Vistas disponibles del personaje */}
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {views.map((v) => {
                            const vAsset = assetById[v.assetId];
                            return (
                              <button
                                key={v.id || v.assetId}
                                type="button"
                                data-testid={`pick-char-view-${char.id}-${v.id || v.assetId}`}
                                onClick={() => handleAddCharacterView(char, v)}
                                className="flex items-center gap-1.5 rounded border border-[#303030] bg-[#181818] px-2 py-1 text-[11px] text-[#d8d4cd] hover:border-[#f5b544] hover:bg-[#f5b544]/10 transition-colors"
                              >
                                <div className="h-4 w-4 rounded overflow-hidden bg-black shrink-0">
                                  <MediaFrame asset={vAsset} fit="cover" className="h-full w-full" />
                                </div>
                                <span>{v.label || "Vista"}</span>
                                <span className="text-[9px] text-[#737373]">({v.viewType || "frontal"})</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Otras referencias autorizadas del proyecto */}
              {projectReferences.length > 0 && (
                <div className="mt-2 flex flex-col gap-2">
                  <span className="text-[11px] font-semibold uppercase text-[#a3a3a3]">
                    Otras referencias ({projectReferences.length})
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {projectReferences.map((ref) => {
                      const rAsset = assetById[ref.assetId];
                      return (
                        <button
                          key={ref.id}
                          type="button"
                          data-testid={`pick-project-ref-${ref.id}`}
                          onClick={() => handleAddDirectRef(ref)}
                          className="flex items-center gap-2 rounded border border-[#303030] bg-[#101010] p-1.5 pr-2.5 text-xs text-[#ededed] hover:border-[#f5b544] hover:bg-[#f5b544]/10"
                        >
                          <div className="h-6 w-6 rounded overflow-hidden bg-black shrink-0">
                            <MediaFrame asset={rAsset} fit="cover" className="h-full w-full" />
                          </div>
                          <span className="text-[11px]">{ref.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-end pt-2 border-t border-[#262626]">
              <button
                type="button"
                onClick={() => setShowAddRefModal(false)}
                className="v-btn-ghost v-btn-sm"
              >
                Cerrar
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Diálogo de confirmación antes de reemplazar el medio activo de la toma */}
      <ConfirmDialog
        open={replaceConfirmOpen}
        onOpenChange={setReplaceConfirmOpen}
        title="¿Reemplazar medio activo de la toma?"
        body={`La toma "${currentScene?.title || `Toma ${sceneIndex}`}" ya tiene un medio asignado. Al asignar esta variante, el medio anterior se conservará intacto en la biblioteca y en el historial de la toma.`}
        confirmLabel="Reemplazar medio activo"
        destructive={false}
        onConfirm={executeAssignMedia}
      />

      {/* Diálogo de confirmación explícita de duración de montaje */}
      <Dialog open={confirmDurationOpen} onOpenChange={setConfirmDurationOpen}>
        <DialogContent
          data-testid="confirm-duration-dialog"
          className="max-w-md border border-[#303030] bg-[#181818] p-5 text-slate-100 shadow-2xl"
        >
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-[#f4f1ec] flex items-center gap-2">
              <Clock size={16} className="text-[#f5b544]" />
              Confirmar duración de montaje ({resolvedMontage.duration} s)
            </DialogTitle>
            <DialogDescription className="text-xs text-[#a3a3a3] mt-2 space-y-2 leading-relaxed">
              <span>
                Esta acción establecerá la duración de montaje de la toma{" "}
                <strong className="text-[#f4f1ec]">
                  «{currentScene?.title || `Toma ${sceneIndex}`}»
                </strong>{" "}
                en <strong className="text-[#f5b544]">{resolvedMontage.duration} s</strong> en el Storyboard del proyecto activo, sin cambiar la duración generada ni los parámetros del nodo de vídeo.
              </span>
              <span className="block text-[11px] text-[#777] font-mono mt-1">
                Procedencia: {resolvedMontage.details || resolvedMontage.source}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex items-center justify-end gap-2 border-t border-[#262626] pt-3">
            <button
              type="button"
              data-testid="cancel-duration-btn"
              onClick={() => setConfirmDurationOpen(false)}
              className="v-btn-ghost v-btn-sm text-xs"
            >
              Cancelar
            </button>
            <button
              type="button"
              data-testid="save-duration-confirm-btn"
              onClick={() => {
                // Revalidar antes de guardar
                const activeProjectId = data?.project?.id || state.projects?.find((p) => p.id === currentScene?.projectId)?.id;
                const freshScenes = state.scenes || data.scenes || [];
                const freshScene = freshScenes.find((s) => s.id === currentScene?.id);

                if (!freshScene) {
                  toast.error("La toma ya no existe en el proyecto.");
                  setConfirmDurationOpen(false);
                  return;
                }

                if (activeProjectId && freshScene.projectId && freshScene.projectId !== activeProjectId) {
                  toast.error("La toma no pertenece al proyecto activo.");
                  setConfirmDurationOpen(false);
                  return;
                }

                // No sobrescribir una duración ya guardada
                if (typeof freshScene.duration === "number" && freshScene.duration > 0) {
                  toast.info("La toma ya cuenta con una duración consolidada en la escena.");
                  setConfirmDurationOpen(false);
                  return;
                }

                // Re-evaluar duración recuperable con las propuestas actuales
                const rechecked = resolveSceneDuration(freshScene, {
                  proposals: allProposals,
                  activeScript: data.activeScript,
                  projectId: activeProjectId,
                });

                if (!rechecked.isRecovered || typeof rechecked.duration !== "number" || rechecked.duration <= 0) {
                  toast.error("La propuesta ya no es recuperable o no está aceptada.");
                  setConfirmDurationOpen(false);
                  return;
                }

                // Confirmar debe actualizar únicamente la duración de la toma correspondiente dentro del proyecto activo
                dispatch({
                  type: "SCENE_UPDATE",
                  payload: {
                    id: freshScene.id,
                    patch: {
                      duration: rechecked.duration,
                    },
                  },
                });

                setConfirmDurationOpen(false);
                toast.success(`Duración de montaje de ${rechecked.duration} s confirmada en la escena.`);
              }}
              className="v-btn-primary v-btn-sm text-xs font-semibold inline-flex items-center gap-1"
            >
              <CheckCircle2 size={13} />
              Confirmar duración de {resolvedMontage.duration} s
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const NodeConfigModal = ({
  open,
  onClose,
  node,
  data,
  onRun,
  hasOperational,
}) => {
  if (!open || !node) return null;
  return (
    <NodeConfigModalContent
      open={open}
      onClose={onClose}
      node={node}
      data={data}
      onRun={onRun}
      hasOperational={hasOperational}
    />
  );
};

export default NodeConfigModal;
